import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Popover,
  ButtonBase,
  TextField,
  MenuItem,
  Divider,
  Button,
  Stack,
  InputAdornment,
  Menu,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StorageIcon from "@mui/icons-material/Storage";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const SECTION_PROFILE = "profile";
const SECTION_DATA_SOURCE = "data-source";
const SECTION_NOTIFICATIONS = "notifications";
const SECTION_CHANGE_PASSWORD = "change-password";
const FRONTEND_LOGIN_USER_KEY = "frontend_login_username";
const FRONTEND_LOGIN_PASSWORD_KEY = "frontend_login_password";
const PROFILE_SETTINGS_KEY = "frontend_profile_settings";
const DATA_SOURCE_SETTINGS_KEY = "frontend_data_source_settings";
const NOTIFICATION_SETTINGS_KEY = "frontend_notification_settings";
const DEFAULT_FRONTEND_USERNAME = "brokancode";
const DEFAULT_FRONTEND_PASSWORD = "pharmaron2025";
const DEFAULT_PROFILE_SETTINGS = {
  firstName: "Matthew",
  lastName: "Beatty",
  email: "m.beatty@brokancode.com",
  password: "password",
  photoUrl: "",
  avatarColor: "#e64a19",
};
const DEFAULT_DATA_SOURCE_SETTINGS = {
  platform: "Crunchbase",
  username: "username",
  password: "password",
};
const DEFAULT_NOTIFICATION_SETTINGS = {
  email: "m.beatty@brokancode.com",
  method: "Email",
  frequency: "Weekly",
};

const sectionOptions = [
  {
    id: SECTION_PROFILE,
    label: "Profile",
    icon: PersonOutlineIcon,
  },
  {
    id: SECTION_DATA_SOURCE,
    label: "Data Source Account",
    icon: StorageIcon,
  },
  {
    id: SECTION_NOTIFICATIONS,
    label: "Notification Frequency",
    icon: NotificationsNoneIcon,
  },
  {
    id: SECTION_CHANGE_PASSWORD,
    label: "Change Password",
    icon: LockResetIcon,
  },
];

function buildInitials(firstName, lastName) {
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "MB";
}

function loadStoredSettings(storageKey, fallbackValue) {
  try {
    const storedValue = localStorage.getItem(storageKey);
    if (!storedValue) return fallbackValue;

    return {
      ...fallbackValue,
      ...JSON.parse(storedValue),
    };
  } catch {
    return fallbackValue;
  }
}

function ColorOption({ color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          backgroundColor: color,
          border: "1px solid rgba(15, 23, 42, 0.14)",
        }}
      />
      <span>{label}</span>
    </Box>
  );
}

function ProfileSection({
  profile,
  setProfile,
  onPhotoUpload,
  onPhotoRemove,
  showPassword,
  onTogglePasswordVisibility,
}) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TextField
        size="small"
        label="First Name"
        value={profile.firstName}
        onChange={(e) =>
          setProfile((prev) => ({ ...prev, firstName: e.target.value }))
        }
      />
      <TextField
        size="small"
        label="Surname"
        value={profile.lastName}
        onChange={(e) =>
          setProfile((prev) => ({ ...prev, lastName: e.target.value }))
        }
      />
      <TextField
        size="small"
        label="Email Address"
        type="email"
        value={profile.email}
        onChange={(e) =>
          setProfile((prev) => ({ ...prev, email: e.target.value }))
        }
      />
      <TextField
        size="small"
        label="Password"
        type={showPassword ? "text" : "password"}
        value={profile.password}
        onChange={(e) =>
          setProfile((prev) => ({ ...prev, password: e.target.value }))
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onTogglePasswordVisibility} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Button
          variant="outlined"
          component="label"
          sx={{ textTransform: "none" }}
        >
          Upload Photo
          <input hidden accept="image/*" type="file" onChange={onPhotoUpload} />
        </Button>
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          (Optional)
        </Typography>
        <Button
          variant="text"
          color="inherit"
          onClick={onPhotoRemove}
          disabled={!profile.photoUrl}
          sx={{ textTransform: "none", minWidth: 0, px: 0.5 }}
        >
          Remove Photo
        </Button>
      </Stack>
      <TextField
        size="small"
        select
        label="Avatar Background"
        value={profile.avatarColor}
        onChange={(e) =>
          setProfile((prev) => ({ ...prev, avatarColor: e.target.value }))
        }
      >
        <MenuItem value="#e64a19">
          <ColorOption color="#e64a19" label="Orange" />
        </MenuItem>
        <MenuItem value="#1565c0">
          <ColorOption color="#1565c0" label="Blue" />
        </MenuItem>
        <MenuItem value="#2e7d32">
          <ColorOption color="#2e7d32" label="Green" />
        </MenuItem>
        <MenuItem value="#6a1b9a">
          <ColorOption color="#6a1b9a" label="Plum" />
        </MenuItem>
        <MenuItem value="#374151">
          <ColorOption color="#374151" label="Slate" />
        </MenuItem>
        <MenuItem value="#ffffff">
          <ColorOption color="#ffffff" label="White" />
        </MenuItem>
        <MenuItem value="transparent">
          <ColorOption color="transparent" label="None" />
        </MenuItem>
      </TextField>
    </Box>
  );
}

function DataSourceSection({
  dataSource,
  setDataSource,
  showPassword,
  onTogglePasswordVisibility,
}) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TextField
        size="small"
        select
        label="Platform"
        value={dataSource.platform}
        onChange={(e) =>
          setDataSource((prev) => ({ ...prev, platform: e.target.value }))
        }
      >
        <MenuItem value="Crunchbase">Crunchbase</MenuItem>
        <MenuItem value="GlobalData">GlobalData</MenuItem>
        <MenuItem value="Company House">Company House</MenuItem>
      </TextField>
      <TextField
        size="small"
        label="Username"
        value={dataSource.username}
        onChange={(e) =>
          setDataSource((prev) => ({ ...prev, username: e.target.value }))
        }
      />
      <TextField
        size="small"
        label="Password"
        type={showPassword ? "text" : "password"}
        value={dataSource.password}
        onChange={(e) =>
          setDataSource((prev) => ({ ...prev, password: e.target.value }))
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onTogglePasswordVisibility} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

function NotificationsSection({ notifications, setNotifications }) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TextField
        size="small"
        label="Notification Email"
        type="email"
        value={notifications.email}
        onChange={(e) =>
          setNotifications((prev) => ({ ...prev, email: e.target.value }))
        }
      />
      <TextField
        size="small"
        select
        label="Delivery Method"
        value={notifications.method}
        onChange={(e) =>
          setNotifications((prev) => ({ ...prev, method: e.target.value }))
        }
      >
        <MenuItem value="Email">Email</MenuItem>
      </TextField>
      <TextField
        size="small"
        select
        label="Frequency"
        value={notifications.frequency}
        onChange={(e) =>
          setNotifications((prev) => ({ ...prev, frequency: e.target.value }))
        }
      >
        <MenuItem value="Daily">Daily</MenuItem>
        <MenuItem value="Weekly">Weekly</MenuItem>
        <MenuItem value="Monthly">Monthly</MenuItem>
      </TextField>
    </Box>
  );
}

function ChangePasswordSection({
  frontendLogin,
  setFrontendLogin,
  frontendPasswordError,
  showFrontendLoginCurrentPassword,
  showFrontendLoginNewPassword,
  showFrontendLoginConfirmPassword,
  onToggleFrontendLoginCurrentPasswordVisibility,
  onToggleFrontendLoginNewPasswordVisibility,
  onToggleFrontendLoginConfirmPasswordVisibility,
}) {
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
        This password is used only for logging into the frontend.
      </Typography>
      <TextField
        size="small"
        label="Username"
        value={frontendLogin.username}
        disabled
      />
      <TextField
        size="small"
        label="Current Password"
        type={showFrontendLoginCurrentPassword ? "text" : "password"}
        value={frontendLogin.currentPassword}
        onChange={(e) =>
          setFrontendLogin((prev) => ({
            ...prev,
            currentPassword: e.target.value,
          }))
        }
        error={Boolean(frontendPasswordError)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onToggleFrontendLoginCurrentPasswordVisibility} edge="end">
                {showFrontendLoginCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        size="small"
        label="New Password"
        type={showFrontendLoginNewPassword ? "text" : "password"}
        value={frontendLogin.newPassword}
        onChange={(e) =>
          setFrontendLogin((prev) => ({
            ...prev,
            newPassword: e.target.value,
          }))
        }
        error={Boolean(frontendPasswordError)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onToggleFrontendLoginNewPasswordVisibility} edge="end">
                {showFrontendLoginNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        size="small"
        label="Confirm New Password"
        type={showFrontendLoginConfirmPassword ? "text" : "password"}
        value={frontendLogin.confirmPassword}
        onChange={(e) =>
          setFrontendLogin((prev) => ({
            ...prev,
            confirmPassword: e.target.value,
          }))
        }
        error={Boolean(frontendPasswordError)}
        helperText={frontendPasswordError || "Enter password twice to confirm the change."}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onToggleFrontendLoginConfirmPasswordVisibility} edge="end">
                {showFrontendLoginConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

export default function Header({ onExportFeedback }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [activeSection, setActiveSection] = useState(SECTION_PROFILE);
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const [showDataSourcePassword, setShowDataSourcePassword] = useState(false);
  const [showFrontendLoginCurrentPassword, setShowFrontendLoginCurrentPassword] = useState(false);
  const [showFrontendLoginNewPassword, setShowFrontendLoginNewPassword] = useState(false);
  const [showFrontendLoginConfirmPassword, setShowFrontendLoginConfirmPassword] = useState(false);
  const [frontendPasswordError, setFrontendPasswordError] = useState("");
  const [savedProfile, setSavedProfile] = useState(() =>
    loadStoredSettings(PROFILE_SETTINGS_KEY, DEFAULT_PROFILE_SETTINGS)
  );
  const [draftProfile, setDraftProfile] = useState(() =>
    loadStoredSettings(PROFILE_SETTINGS_KEY, DEFAULT_PROFILE_SETTINGS)
  );
  const [savedDataSource, setSavedDataSource] = useState(() =>
    loadStoredSettings(DATA_SOURCE_SETTINGS_KEY, DEFAULT_DATA_SOURCE_SETTINGS)
  );
  const [draftDataSource, setDraftDataSource] = useState(() =>
    loadStoredSettings(DATA_SOURCE_SETTINGS_KEY, DEFAULT_DATA_SOURCE_SETTINGS)
  );
  const [savedNotifications, setSavedNotifications] = useState(() =>
    loadStoredSettings(NOTIFICATION_SETTINGS_KEY, DEFAULT_NOTIFICATION_SETTINGS)
  );
  const [draftNotifications, setDraftNotifications] = useState(() =>
    loadStoredSettings(NOTIFICATION_SETTINGS_KEY, DEFAULT_NOTIFICATION_SETTINGS)
  );
  const [savedFrontendLogin, setSavedFrontendLogin] = useState({
    username: localStorage.getItem(FRONTEND_LOGIN_USER_KEY) || DEFAULT_FRONTEND_USERNAME,
    password: localStorage.getItem(FRONTEND_LOGIN_PASSWORD_KEY) || DEFAULT_FRONTEND_PASSWORD,
  });
  const [draftFrontendLogin, setDraftFrontendLogin] = useState({
    username: localStorage.getItem(FRONTEND_LOGIN_USER_KEY) || DEFAULT_FRONTEND_USERNAME,
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [exportFileName, setExportFileName] = useState("company_feedback_export");

  const initials = useMemo(
    () => buildInitials(savedProfile.firstName, savedProfile.lastName),
    [savedProfile.firstName, savedProfile.lastName]
  );

  const fullName = `${savedProfile.firstName} ${savedProfile.lastName}`.trim();
  const exportMenuOpen = Boolean(exportAnchor);

  useEffect(() => {
    localStorage.setItem(PROFILE_SETTINGS_KEY, JSON.stringify(savedProfile));
  }, [savedProfile]);

  useEffect(() => {
    localStorage.setItem(DATA_SOURCE_SETTINGS_KEY, JSON.stringify(savedDataSource));
  }, [savedDataSource]);

  useEffect(() => {
    localStorage.setItem(
      NOTIFICATION_SETTINGS_KEY,
      JSON.stringify(savedNotifications)
    );
  }, [savedNotifications]);

  const openMenu = (event) => {
    setDraftProfile(savedProfile);
    setDraftDataSource(savedDataSource);
    setDraftNotifications(savedNotifications);
    setDraftFrontendLogin({
      username: savedFrontendLogin.username,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setFrontendPasswordError("");
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setDraftProfile(savedProfile);
    setDraftDataSource(savedDataSource);
    setDraftNotifications(savedNotifications);
    setDraftFrontendLogin({
      username: savedFrontendLogin.username,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowProfilePassword(false);
    setShowDataSourcePassword(false);
    setShowFrontendLoginCurrentPassword(false);
    setShowFrontendLoginNewPassword(false);
    setShowFrontendLoginConfirmPassword(false);
    setFrontendPasswordError("");
    setMenuAnchor(null);
  };

  const openExportMenu = (event) => {
    setExportAnchor(event.currentTarget);
  };

  const closeExportMenu = () => {
    setExportAnchor(null);
  };

  const handleExportDownload = () => {
    onExportFeedback?.(exportFileName);
    setExportAnchor(null);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setDraftProfile((prev) => ({
        ...prev,
        photoUrl: typeof reader.result === "string" ? reader.result : "",
      }));
      event.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoRemove = () => {
    setDraftProfile((prev) => ({
      ...prev,
      photoUrl: "",
    }));
  };

  const handleSaveChanges = () => {
    const passwordChangeRequested =
      draftFrontendLogin.currentPassword ||
      draftFrontendLogin.newPassword ||
      draftFrontendLogin.confirmPassword;

    if (passwordChangeRequested) {
      if (draftFrontendLogin.currentPassword !== savedFrontendLogin.password) {
        setActiveSection(SECTION_CHANGE_PASSWORD);
        setFrontendPasswordError("Current password is incorrect.");
        return;
      }

      if (!draftFrontendLogin.newPassword) {
        setActiveSection(SECTION_CHANGE_PASSWORD);
        setFrontendPasswordError("Enter a new password.");
        return;
      }

      if (draftFrontendLogin.newPassword !== draftFrontendLogin.confirmPassword) {
        setActiveSection(SECTION_CHANGE_PASSWORD);
        setFrontendPasswordError("New password and confirmation must match.");
        return;
      }
    }

    setSavedProfile(draftProfile);
    setSavedDataSource(draftDataSource);
    setSavedNotifications(draftNotifications);
    if (passwordChangeRequested) {
      const nextFrontendLogin = {
        username: savedFrontendLogin.username,
        password: draftFrontendLogin.newPassword,
      };

      setSavedFrontendLogin(nextFrontendLogin);
      localStorage.setItem(FRONTEND_LOGIN_USER_KEY, nextFrontendLogin.username);
      localStorage.setItem(FRONTEND_LOGIN_PASSWORD_KEY, nextFrontendLogin.password);
    }
    setShowProfilePassword(false);
    setShowDataSourcePassword(false);
    setShowFrontendLoginCurrentPassword(false);
    setShowFrontendLoginNewPassword(false);
    setShowFrontendLoginConfirmPassword(false);
    setFrontendPasswordError("");
    setMenuAnchor(null);
  };

  const renderActiveSection = () => {
    if (activeSection === SECTION_PROFILE) {
      return (
        <ProfileSection
          profile={draftProfile}
          setProfile={setDraftProfile}
          onPhotoUpload={handlePhotoUpload}
          onPhotoRemove={handlePhotoRemove}
          showPassword={showProfilePassword}
          onTogglePasswordVisibility={() =>
            setShowProfilePassword((prev) => !prev)
          }
        />
      );
    }

    if (activeSection === SECTION_CHANGE_PASSWORD) {
      return (
        <ChangePasswordSection
          frontendLogin={draftFrontendLogin}
          setFrontendLogin={setDraftFrontendLogin}
          frontendPasswordError={frontendPasswordError}
          showFrontendLoginCurrentPassword={showFrontendLoginCurrentPassword}
          showFrontendLoginNewPassword={showFrontendLoginNewPassword}
          showFrontendLoginConfirmPassword={showFrontendLoginConfirmPassword}
          onToggleFrontendLoginCurrentPasswordVisibility={() =>
            setShowFrontendLoginCurrentPassword((prev) => !prev)
          }
          onToggleFrontendLoginNewPasswordVisibility={() =>
            setShowFrontendLoginNewPassword((prev) => !prev)
          }
          onToggleFrontendLoginConfirmPasswordVisibility={() =>
            setShowFrontendLoginConfirmPassword((prev) => !prev)
          }
        />
      );
    }

    if (activeSection === SECTION_DATA_SOURCE) {
      return (
        <DataSourceSection
          dataSource={draftDataSource}
          setDataSource={setDraftDataSource}
          showPassword={showDataSourcePassword}
          onTogglePasswordVisibility={() =>
            setShowDataSourcePassword((prev) => !prev)
          }
        />
      );
    }

    return (
      <NotificationsSection
        notifications={draftNotifications}
        setNotifications={setDraftNotifications}
      />
    );
  };

  return (
    <AppBar
      position="fixed"
      sx={{ backgroundColor: "#ffffff", boxShadow: "0px 2px 5px rgba(0,0,0,0.1)" }}
    >
      <Toolbar
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "88px minmax(0, 1fr) auto",
            md: "104px minmax(0, 1fr) auto",
          },
          alignItems: "center",
          gap: 1.5,
          minHeight: { xs: 68, md: 76 },
          px: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifySelf: "start",
            gap: 1,
          }}
        >
          <Box
            component="img"
            src="/brokancode.png"
            alt="Brokancode"
            sx={{ height: { xs: 38, md: 48 } }}
          />

          <Button
            variant="contained"
            color="success"
            onClick={openExportMenu}
            endIcon={<KeyboardArrowDownIcon />}
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              whiteSpace: "nowrap",
              px: 1.5,
              py: 0.6,
              minHeight: 36,
              display: { xs: "none", md: "inline-flex" },
              boxShadow: "none",
              borderRadius: 999,
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
              "&:hover": {
                boxShadow: "none",
                background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
              },
            }}
          >
            Export CSV
          </Button>
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: "#1f2937",
            textAlign: "center",
            fontWeight: 700,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: 1.2,
            px: 1,
          }}
        >
          Brokancode - Pharmaron: Lead Discovery Demo
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifySelf: "end",
            gap: 1,
          }}
        >
          <IconButton
            onClick={openMenu}
            sx={{
              p: 0.25,
              border: "1px solid rgba(15, 23, 42, 0.12)",
              borderRadius: 999,
            }}
          >
            <Avatar
              src={savedProfile.photoUrl || undefined}
              sx={{
                width: 38,
                height: 38,
                bgcolor: savedProfile.avatarColor,
                fontSize: "0.95rem",
                fontWeight: 700,
              }}
            >
              {!savedProfile.photoUrl ? initials : null}
            </Avatar>
          </IconButton>

          <Box
            component="img"
            src="/pharmaron.png"
            alt="Pharmaron"
            sx={{ height: { xs: 34, md: 44 }, justifySelf: "end" }}
          />
        </Box>
      </Toolbar>

      <Menu
        anchorEl={exportAnchor}
        open={exportMenuOpen}
        onClose={closeExportMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1.5,
            minWidth: 280,
            borderRadius: 3,
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Box sx={{ display: "grid", gap: 1.25 }}>
          <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
            Export Feedback CSV
          </Typography>
          <TextField
            size="small"
            label="File Name"
            value={exportFileName}
            onChange={(e) => setExportFileName(e.target.value)}
          />
          <Button
            variant="contained"
            color="success"
            onClick={handleExportDownload}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Download
          </Button>
        </Box>
      </Menu>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: { xs: 360, md: 500 },
            mt: 1.5,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Box sx={{ p: 2, background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "64px minmax(0, 1fr)",
              gap: 1.5,
              alignItems: "center",
              p: 1.5,
              borderRadius: 2.5,
              backgroundColor: "rgba(255,255,255,0.8)",
            }}
          >
            <Avatar
              src={savedProfile.photoUrl || undefined}
              sx={{
                width: 56,
                height: 56,
                bgcolor: savedProfile.avatarColor,
                fontSize: "1.35rem",
                fontWeight: 700,
              }}
            >
              {!savedProfile.photoUrl ? initials : null}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#1f2937", lineHeight: 1.15 }}
              >
                {fullName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.35, color: "#64748b", wordBreak: "break-word" }}
              >
                {savedProfile.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "170px minmax(0, 1fr)" },
            minHeight: 360,
          }}
        >
          <Box
            sx={{
              borderRight: { md: "1px solid rgba(15, 23, 42, 0.08)" },
              borderBottom: { xs: "1px solid rgba(15, 23, 42, 0.08)", md: "none" },
              p: 1,
              display: "grid",
              gap: 0.5,
              alignContent: "start",
            }}
          >
            {sectionOptions.map((section) => {
              const Icon = section.icon;
              const active = activeSection === section.id;

              return (
                <ButtonBase
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    borderRadius: 2,
                    px: 1.25,
                    py: 1,
                    gap: 1.25,
                    backgroundColor: active ? "rgba(37, 99, 235, 0.08)" : "transparent",
                    color: active ? "#1d4ed8" : "#334155",
                  }}
                >
                  <Icon fontSize="small" />
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: active ? 700 : 500 }}>
                    {section.label}
                  </Typography>
                </ButtonBase>
              );
            })}

            <Divider sx={{ my: 0.75 }} />

            <ButtonBase
              onClick={() => {
                localStorage.removeItem("lead_auth");
                window.location.reload();
              }}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                borderRadius: 2,
                px: 1.25,
                py: 1,
                gap: 1.25,
                color: "#b91c1c",
              }}
            >
              <LogoutIcon fontSize="small" />
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
                Log out
              </Typography>
            </ButtonBase>
          </Box>

          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: "#1f2937" }}>
              {sectionOptions.find((section) => section.id === activeSection)?.label}
            </Typography>

            {renderActiveSection()}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" onClick={handleSaveChanges} sx={{ textTransform: "none" }}>
                Save Changes
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </AppBar>
  );
}
